import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getComplianceMetricsTrends, getComplianceComparison } from "@/services/analyticsService";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, TrendingDown, Clock, Target, CheckCircle, Minus } from "lucide-react";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Badge } from "@/components/ui/badge";


const ChangeIndicator = ({ value, inverse = false }: { value: number; inverse?: boolean }) => {
  const isPositive = inverse ? value < 0 : value > 0;
  const isNeutral = Math.abs(value) < 0.1;
  
  if (isNeutral) {
    return (
      <Badge variant="secondary" className="gap-1">
        <Minus className="h-3 w-3" />
        <span>{Math.abs(value).toFixed(1)}%</span>
      </Badge>
    );
  }
  
  return (
    <Badge variant={isPositive ? "default" : "destructive"} className="gap-1">
      {isPositive ? (
        <TrendingUp className="h-3 w-3" />
      ) : (
        <TrendingDown className="h-3 w-3" />
      )}
      <span>{Math.abs(value).toFixed(1)}%</span>
    </Badge>
  );
};

export const ComplianceMetricsDashboard = () => {
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });


  const { data: trends = [], isLoading } = useQuery({
    queryKey: ['compliance-trends', startDate, endDate],
    queryFn: () => getComplianceMetricsTrends(new Date(startDate), new Date(endDate)),
  });

  const { data: comparison } = useQuery({
    queryKey: ['compliance-comparison', startDate, endDate],
    queryFn: () => getComplianceComparison(new Date(startDate), new Date(endDate)),
  });

  const latestMetrics = trends.length > 0 ? trends[trends.length - 1] : null;
  const avgApprovalRate = trends.length > 0 
    ? trends.reduce((sum, t) => sum + t.approvalRate, 0) / trends.length 
    : 0;
  const avgReviewTime = trends.length > 0 
    ? trends.reduce((sum, t) => sum + t.avgReviewTimeHours, 0) / trends.length 
    : 0;
  const avgAIAccuracy = trends.length > 0 
    ? trends.reduce((sum, t) => sum + t.aiAccuracy, 0) / trends.length 
    : 0;

  const chartConfig = {
    approvalRate: {
      label: "Approval Rate",
      color: "hsl(var(--chart-1))",
    },
    avgReviewTimeHours: {
      label: "Review Time (hrs)",
      color: "hsl(var(--chart-2))",
    },
    aiAccuracy: {
      label: "AI Accuracy",
      color: "hsl(var(--chart-3))",
    },
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-foreground">Compliance Metrics</h2>
        <p className="text-muted-foreground">Track approval rates, review times, and AI performance</p>
      </div>

      <div className="flex gap-4">
        <div className="space-y-2">
          <Label htmlFor="start-date">Start Date</Label>
          <Input
            id="start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="end-date">End Date</Label>
          <Input
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {comparison && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle>Period Comparison</CardTitle>
            <CardDescription>Current period vs previous period of same length</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Approval Rate</div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold">{comparison.current.approvalRate.toFixed(1)}%</div>
                    <div className="text-xs text-muted-foreground">
                      Previous: {comparison.previous.approvalRate.toFixed(1)}%
                    </div>
                  </div>
                  <ChangeIndicator value={comparison.changes.approvalRate} />
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Avg Review Time</div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold">{comparison.current.avgReviewTimeHours.toFixed(1)}h</div>
                    <div className="text-xs text-muted-foreground">
                      Previous: {comparison.previous.avgReviewTimeHours.toFixed(1)}h
                    </div>
                  </div>
                  <ChangeIndicator value={comparison.changes.avgReviewTimeHours} inverse />
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">AI Accuracy</div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold">{comparison.current.aiAccuracy.toFixed(1)}%</div>
                    <div className="text-xs text-muted-foreground">
                      Previous: {comparison.previous.aiAccuracy.toFixed(1)}%
                    </div>
                  </div>
                  <ChangeIndicator value={comparison.changes.aiAccuracy} />
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Total Deals</div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold">{comparison.current.totalDeals}</div>
                    <div className="text-xs text-muted-foreground">
                      Previous: {comparison.previous.totalDeals}
                    </div>
                  </div>
                  <ChangeIndicator value={comparison.changes.totalDeals} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Approval Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgApprovalRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Current: {latestMetrics?.approvalRate.toFixed(1) || 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Review Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgReviewTime.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground">
              Current: {latestMetrics?.avgReviewTimeHours.toFixed(1) || 0}h
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Accuracy</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgAIAccuracy.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Current: {latestMetrics?.aiAccuracy.toFixed(1) || 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {trends.reduce((sum, t) => sum + t.totalDeals, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              In selected period
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Approval Rate Trend</CardTitle>
          <CardDescription>Daily approval rate percentage</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Loading...
            </div>
          ) : trends.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No data available for the selected period
            </div>
          ) : (
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    domain={[0, 100]}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line 
                    type="monotone" 
                    dataKey="approvalRate" 
                    stroke="var(--color-approvalRate)"
                    strokeWidth={2}
                    name="Approval Rate %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Review Time Trend</CardTitle>
            <CardDescription>Average hours to review deals</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : trends.length === 0 ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <ChartContainer config={chartConfig} className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="avgReviewTimeHours" 
                      stroke="var(--color-avgReviewTimeHours)"
                      strokeWidth={2}
                      name="Review Time (hrs)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>AI Accuracy Trend</CardTitle>
            <CardDescription>AI recommendation accuracy percentage</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : trends.length === 0 ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <ChartContainer config={chartConfig} className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      domain={[0, 100]}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="aiAccuracy" 
                      stroke="var(--color-aiAccuracy)"
                      strokeWidth={2}
                      name="AI Accuracy %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
